import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ApiErrorResponse } from "../models/ApiResponse";
import { CollectiveAgreement } from "../models/Consultant";
import { isUndefinedOrTrue } from "../utils/object.utils";
import { apiStore } from "../stores/api.store";
import { toast } from "../utils/toast";

export type GetCollectiveAgreementsParams = {
	page: number;
	pageSize: number;
	keyword?: string;
	sort?: string;
	order?: string;
	expand?: string[];
};

export function useCollectiveAgreements(query: GetCollectiveAgreementsParams, enabled?: boolean) {
	const { data, ...rest } = useQuery(
		["collective-agreements", query],
		() =>
			apiStore
				.get<{ totalCount: number; collectiveAgreements: CollectiveAgreement[] }>({
					path: "/collective-agreement",
					isAuthRequired: true,
					query: {
						...query,
						expand: query.expand ? query.expand.join(",") : "",
					},
				})
				.then((d) => d.data),
		{
			keepPreviousData: true,
			staleTime: 60 * 60 * 1000, // 1 hour
			enabled,
		}
	);

	return {
		collectiveAgreementList: data,
		...rest,
	};
}

export function useCollectiveAgreement(agreementId?: string, enabled?: boolean) {
	const { data, ...rest } = useQuery(
		["collective-agreements", agreementId],
		() =>
			apiStore
				.get<CollectiveAgreement>({
					path: `/collective-agreement/${agreementId}`,
					isAuthRequired: true,
				})
				.then((d) => d.data),
		{
			enabled: !!agreementId && isUndefinedOrTrue(enabled),
		}
	);

	return {
		collectiveAgreement: data,
		...rest,
	};
}

export interface CreateCollectiveAgreementRequest {
	employmentType: "fixedTerm" | "guarantee" | "subcontractor";
	guaranteeSalary?: number;
	guaranteedHoursPerMonth?: number;
	guaranteedHoursByMonth?: {
		1: number;
		2: number;
		3: number;
		4: number;
		5: number;
		6: number;
		7: number;
		8: number;
		9: number;
		10: number;
		11: number;
		12: number;
	};
	internalDescription?: string;
	isGflControlled: boolean;
	name: string;
	obCompensation: Array<{
		type: "evening" | "night" | "weekend" | "weekendNight";
		startTime: string;
		endTime: string;
		hourlyRate: number;
	}>;
	pensionType: "itp1" | "itp2" | "notWhiteCollar";
	productionUnit?: string;
	salaryProgramCategory?: string;
	salaryStatistics: {
		wageType: string;
		agreementCode?: string;
		federalNumber?: string;
		occupationCode?: string;
		personnelCategory?: string;
		unionNumber?: string;
		workTimeCode?: string;
		workplaceNumberScb?: string;
		workplaceNumberSn?: string;
	};
	unionType: "noAfa" | "blueCollar" | "whiteCollar";
	vacationSalaryPercent: number;

	// Additional fields
	itpCostCenter?: string;
	itpPlanId?: string;
}

export function useCreateCollectiveAgreement() {
	const queryClient = useQueryClient();

	return useMutation<
		CollectiveAgreement,
		ApiErrorResponse,
		{
			silent?: boolean;
			body: CreateCollectiveAgreementRequest;
		}
	>(
		(params) =>
			apiStore
				.post({
					path: `/collective-agreement`,
					body: params.body,
					isAuthRequired: true,
				})
				.then((d) => d.data),

		{
			onSuccess: (_, params) => {
				if (!params.silent) {
					toast.successGeneric();
				}

				queryClient.invalidateQueries(["collective-agreements"]);
			},
			onError: ({ error }, data) => {
				if (!data.silent) {
					toast.apiError(error.code);
				}
			},
		}
	);
}

export function useUpdateCollectiveAgreement() {
	const queryClient = useQueryClient();

	return useMutation<
		CollectiveAgreement,
		ApiErrorResponse,
		{
			agreementId: string;
			silent?: boolean;
			update: Partial<CreateCollectiveAgreementRequest>;
		}
	>(
		(params) =>
			apiStore
				.put({
					path: `/collective-agreement/${params.agreementId}`,
					body: params.update,
					isAuthRequired: true,
				})
				.then((d) => d.data),

		{
			onSuccess: (_, params) => {
				if (!params.silent) {
					toast.successGeneric();
				}

				queryClient.invalidateQueries(["collective-agreements", params.agreementId]);
				queryClient.invalidateQueries(["collective-agreements"]);
			},
			onError: ({ error }, data) => {
				if (!data.silent) {
					toast.apiError(error.code);
				}
			},
		}
	);
}

export function useDeleteCollectiveAgreement() {
	const queryClient = useQueryClient();

	return useMutation<
		any,
		ApiErrorResponse,
		{
			agreementId: string;
			silent?: boolean;
		}
	>(
		(params) =>
			apiStore
				.delete({
					path: `/collective-agreement/${params.agreementId}`,
					isAuthRequired: true,
				})
				.then((d) => d.data),

		{
			onSuccess: (_, params) => {
				if (!params.silent) {
					toast.successGeneric();
				}

				queryClient.invalidateQueries(["collective-agreements"]);
			},
			onError: ({ error }, data) => {
				if (!data.silent) {
					toast.apiError(error.code);
				}
			},
		}
	);
}
